import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useDropzone } from "react-dropzone";
import {
  Box,
  Typography,
  TextField,
  Tabs,
  Tab,
  Button,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  FormHelperText,
  Chip,
  InputBase,
  useMediaQuery,
  Radio,
  RadioGroup,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { isValidUUIDv4 } from "../hooks/uuid";

import { useMutation, useManualQuery } from "graphql-hooks";
import {
  TOY_DETAIL_BY_UUID,
  UPDATE_TOY_DATA_BY_UUID,
  UPDATE_TOY_IMAGES_BY_UUID,
} from "../graphQL/toy_qry";
import { GET_TOY_ATTRIBUTES_QUERY } from "../graphQL/upload_files_s3";

// Material Icons
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import FormatListBulletedOutlinedIcon from "@mui/icons-material/FormatListBulletedOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import AddIcon from "@mui/icons-material/Add";

import AlertPopup from "../components/AlertPopup";
import FloatingMenu from "../components/FloatingMenu";

const StyledTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: "none",
  "& .MuiTabs-indicator": {
    backgroundColor: "#EF5658",
  },
  "& .MuiTabs-flexContainer": {
    justifyContent: "flex-start",
    gap: "40px",
    "@media (max-width: 1360px)": {
      justifyContent: "space-between",
    },
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  minWidth: "113px",
  fontWeight: 500,
  color: "#666",
  flexDirection: "row",
  "&.Mui-selected": {
    color: "#EF5658",
  },
  "& .MuiSvgIcon-root": {
    marginRight: theme.spacing(1),
    marginBottom: 0,
  },
}));

const StyledFormControl = styled(FormControl)(() => ({
  "& .MuiOutlinedInput-root": { height: "56px" },
  "& fieldset": { borderColor: "rgba(0, 0, 0, 0.23)" },
  "& .MuiInputLabel-asterisk": { color: "#ef5658 !important" },
  "& .MuiFormLabel-asterisk": { color: "#ef5658 !important" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(0, 0, 0, 0.23)",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(0, 0, 0, 0.87)",
    borderWidth: "1px",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "rgba(0, 0, 0, 0.87)",
  },
}));

const StyledOptionalFormControl = styled(FormControl)(() => ({
  "& .MuiOutlinedInput-root": { height: "56px" },
  "& fieldset": { borderColor: "rgba(0, 0, 0, 0.23)" },
  "& .MuiInputLabel-root": { color: "black" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(0, 0, 0, 0.23)",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(0, 0, 0, 0.87)",
    borderWidth: "1px",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "rgba(0, 0, 0, 0.87)",
  },
}));

const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`edit-tabpanel-${index}`}
      aria-labelledby={`edit-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const ToyEdit = () => {
  const { toyUuid } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const isMobile = useMediaQuery("(max-width:1024px)");
  const [floatMenuLeftMargin, setFloatMenuLeftMargin] = useState(0);
  const [formData, setFormData] = useState({});
  const [images, setImages] = useState([]);
  const [originalImages, setOriginalImages] = useState([]);
  const navigate = useNavigate();

  // Popup states
  const [showPopup, setShowPopup] = useState(false);
  const [alertPopup, setAlertPopup] = useState({
    open: false,
  });
  const [isInvalidURL, setIsInvalidURL] = useState(false);
  const [popupConfig, setPopupConfig] = useState({
    title: "",
    message: "",
    showCheckCircle: false,
    confirmText: "Close",
    cancelText: "CANCEL",
    onConfirm: null,
  });

  // Form validation errors
  const [errors, setErrors] = useState({
    title: "",
    description: "",
    price: "",
    images: "",
    brand: "",
    condition: "",
    warnings: "",
    category: "",
    subCategory: "",
  });

  // Tags state
  const [tagValue, setTagValue] = useState("");
  const [tagError, setTagError] = useState("");

  // Track original form data to detect changes
  const [originalFormData, setOriginalFormData] = useState({});

  // Get data from database
  const [categoryList, setCategoryList] = useState([]);
  const [subCategory, setSubCategory] = useState("Don't Care");
  const [subCategoryList, setSubCategoryList] = useState([]);
  const [dbSubCategory, setDbSubCategory] = useState("");
  const [curSubcategoryList, setCurSubcategoryList] = useState([]);
  const [materialList, setMaterialList] = useState([]);
  const [colorList, setColorList] = useState([]);
  const [subColorList, setSubColorList] = useState([]);
  const [ageGroupList, setAgeGroupList] = useState([]);
  const [difficultyList, setDifficultyList] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [brandList, setBrandList] = useState([]);
  const [conditionList, setConditionList] = useState([]);
  const [warningList, setWarningList] = useState([]);
  let role = localStorage.getItem("role") ? localStorage.getItem("role") : null;

  const maxImgSize = 5 * 1024 * 1024; // Max size is 5 MB
  const maxImgNum = 6; // Max image to upload

  const userUuid = localStorage.getItem("userUuid");
  const [getToyAttributes] = useManualQuery(TOY_DETAIL_BY_UUID);
  const [getToyListAttrs] = useManualQuery(GET_TOY_ATTRIBUTES_QUERY);
  const [updateToyAttrData] = useMutation(UPDATE_TOY_DATA_BY_UUID);
  const [updateToyImages] = useMutation(UPDATE_TOY_IMAGES_BY_UUID);

  const setImagesVal = db => {
    const imagesData =
      db.toyImages?.map((img, index) => {
        const fileName = img.fileName.split("/").pop();
        return {
          id: index + 1,
          src: img.fileName,
          name: fileName,
          size: 0,
        };
      }) || [];

    setImages(imagesData);
    setOriginalImages(imagesData);
  };

  useEffect(() => {
    setToyAttributeslist();
    setToyListAttrs();
  }, []);

  const normalizeToTitleList = dataArray => {
    if (!Array.isArray(dataArray)) return [];
    return dataArray
      .filter(item => item && item.title)
      .map(item => item.title.trim())
      .sort((a, b) => a.localeCompare(b));
  };

  const normalizeToNameList = dataArray => {
    if (!Array.isArray(dataArray)) return [];
    return dataArray
      .filter(item => item && item.name)
      .map(item => item.name.trim())
      .sort((a, b) => a.localeCompare(b));
  };
  const setToyListAttrs = async () => {
    const {
      loading: loadingToyAttributes,
      error: errorToyAttributes,
      data: dataToyAttributes,
    } = await getToyListAttrs();
    if (!loadingToyAttributes && dataToyAttributes) {
      setCategoryList(normalizeToTitleList(dataToyAttributes.categories));
      setDbSubCategory(dataToyAttributes.subcategories);
      setBrandList(normalizeToTitleList(dataToyAttributes.brands));
      setAgeGroupList(normalizeToTitleList(dataToyAttributes.ageGroups));
      setWarningList(normalizeToTitleList(dataToyAttributes.warnings));
      setMaterialList(normalizeToTitleList(dataToyAttributes.materials));
      setColorList(normalizeToTitleList(dataToyAttributes.colors));
      setSubColorList(normalizeToTitleList(dataToyAttributes.subColors));
      setDifficultyList(normalizeToTitleList(dataToyAttributes.difficulties));
      setCountryList(normalizeToNameList(dataToyAttributes.manufacturerCountries));
      setConditionList(normalizeToTitleList(dataToyAttributes.conditions));
    }
  };

  useEffect(() => {
    if (dbSubCategory.length > 0) {
      const grouped = dbSubCategory.reduce((acc, sub) => {
        const catTitle = sub.category.title;
        if (!acc[catTitle]) acc[catTitle] = [];
        acc[catTitle].push(sub.title);
        return acc;
      }, {});

      Object.keys(grouped).forEach(key => {
        grouped[key].sort((a, b) => a.localeCompare(b));
      });

      const sortedGrouped = Object.fromEntries(
        Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b))
      );

      setSubCategoryList(sortedGrouped);
      setCurSubcategoryList(sortedGrouped[formData.category]);
    }
  }, [dbSubCategory]);

  const setToyAttributeslist = async () => {
    if (!toyUuid) {
      setIsInvalidURL(true);
      return;
    }
    if (isValidUUIDv4(toyUuid)) {
      const {
        loading: loadingToyAttributes,
        error: errorToyAttributes,
        data: dataToyAttributes,
      } = await getToyAttributes({
        variables: {
          toyUuid,
        },
      });
      if (
        !loadingToyAttributes &&
        dataToyAttributes &&
        dataToyAttributes.toyDetailByUuid
      ) {
        const toyData = dataToyAttributes.toyDetailByUuid;
        /* check if login-user is not owner display error message */
        if (role === "Regular" && toyData.userId.userUuid !== userUuid) {
          setIsInvalidURL(true);
        } else {
          if (
            role === "Regular" &&
            toyData.status !== "For Sale" &&
            toyData.status !== "Draft"
          ) {
            setPopupConfig({
              title: "You cannot edit this toy, because it is already sold.",
              message: "",
              confirmText: "OK",
              cancelText: "",
              onConfirm: null,
            });
            setShowPopup(true);
            return;
          }

          setFormData({
            ...toyData,
            price: toyData.sellingPrice,
            brand: toyData.brandId?.title || "",
            category: toyData.categoryId?.title || "",
            subCategory: toyData.subCategoryId?.title || "",
            condition: toyData.conditionId?.title || "",
            warnings: toyData.warningId?.title || "",
            ageGroup: toyData.ageGroupId?.title || "",
            material: toyData.materialId?.title || "",
            color: toyData.colorId?.title || "",
            subColor: toyData.subColorId?.title || "",
            difficulty: toyData.difficultyId?.title || "",
            manufacturerCountry: toyData.manufacturerCountryId?.name || "",
            tags: toyData.toyTags?.map(t => t.tagId?.title) || [],
            status: toyData.isPublished ? "Published" : "Draft",
          });

          setOriginalFormData({
            ...toyData,
            price: toyData.sellingPrice,
            brand: toyData.brandId?.title || "",
            category: toyData.categoryId?.title || "",
            subCategory: toyData.subCategoryId?.title || "",
            condition: toyData.conditionId?.title || "",
            warnings: toyData.warningId?.title || "",
            ageGroup: toyData.ageGroupId?.title || "",
            material: toyData.materialId?.title || "",
            color: toyData.colorId?.title || "",
            subColor: toyData.subColorId?.title || "",
            difficulty: toyData.difficultyId?.title || "",
            manufacturerCountry: toyData.manufacturerCountryId?.name || "",
            tags: toyData.toyTags?.map(t => t.tagId?.title) || [],
            status: toyData.isPublished ? "Published" : "Draft",
          });

          setImagesVal(toyData);
        }
      } else setIsInvalidURL(true);
      if (errorToyAttributes) {
        console.log("errorToyAttributes: ", errorToyAttributes);
        setIsInvalidURL(true);
      }
    } else setIsInvalidURL(true);
  };
  // End getting data from database

  // Handle Upload Image
  const onDrop = acceptedFiles => {
    showImageFunc(acceptedFiles);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: false,
  });

  const selectImageFunc = event => {
    const fileList = Array.from(event.target.files);
    showImageFunc(fileList);
  };

  const addTimeStampToFilename = fileName => {
    const ext = fileName.substring(fileName.lastIndexOf("."));
    const name = fileName.substring(0, fileName.lastIndexOf("."));
    const timestamp = Date.now();
    return `${name}_${timestamp}${ext}`;
  };

  const showImageFunc = fileList => {
    // Check number of file
    const remainingSlots = maxImgNum - images.length;
    if (remainingSlots <= 0) {
      setPopupConfig({
        title: "Ooops!",
        message: "Sorry! You can upload maximum 6 images",
        confirmText: "OK",
        cancelText: "",
        onConfirm: null,
      });
      setShowPopup(true);
      return;
    }
    // Check file size
    if (fileList[0].size > maxImgSize) {
      setPopupConfig({
        title: "Ooops!",
        message: "Sorry! The maximum size of an image is 5MB.",
        confirmText: "OK",
        cancelText: "",
        onConfirm: null,
      });
      setShowPopup(true);
      return;
    }
    const maxImagesId = Math.max(0, ...images.map(img => Number(img.id) || 0));
    const filesToAdd = fileList.slice(0, remainingSlots);

    const newImages = filesToAdd.map((file, index) => {
      // console.log(file);
      return {
        file,
        id: maxImagesId + index + 1,
        src: URL.createObjectURL(file),
        name: addTimeStampToFilename(file.name),
        size: file.size,
      };
    });
    setImages(prev => [...prev, ...newImages]);
  };

  // End set images

  // Set floating menu place based on screen size
  useEffect(() => {
    const handleResize = () => {
      setFloatMenuLeftMargin(window.innerWidth - 100);
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);
  }, []);

  // Tag validation constants
  const maxTags = 10;
  const maxTagLength = 25;
  const validTagRegex = /^[\w\- ]+$/;

  const getCurrentTabName = () => {
    const tabNames = [
      "Title/Desc",
      "Image",
      "Price",
      "Attributes",
      "Tags",
      "Publish/Draft",
    ];
    return tabNames[activeTab] || "Current";
  };

  const resetCurrentTabData = () => {
    switch (activeTab) {
      case 0: // Title/Desc tab
        setFormData({
          ...formData,
          title: originalFormData.title,
          description: originalFormData.description,
        });
        setErrors({
          ...errors,
          title: "",
          description: "",
        });
        break;

      case 1: // Image tab
        setImages([...originalImages]);
        setErrors({
          ...errors,
          images: "",
        });
        break;

      case 2: // Price tab
        setFormData({
          ...formData,
          price: originalFormData.price,
          originalPrice: originalFormData.originalPrice,
        });
        setErrors({
          ...errors,
          price: "",
        });
        break;

      case 3: // Attributes tab
        setFormData({
          ...formData,
          brand: originalFormData.brand,
          condition: originalFormData.condition,
          warnings: originalFormData.warnings,
          category: originalFormData.category,
          subCategory: originalFormData.subCategory,
          // Optional fields
          ageGroup: originalFormData.ageGroup,
          material: originalFormData.material,
          dimension: originalFormData.dimension,
          weight: originalFormData.weight,
          color: originalFormData.color,
          subColor: originalFormData.subColor,
          noOfPlayers: originalFormData.noOfPlayers,
          batteryRequired: originalFormData.batteryRequired,
          batteryIncluded: originalFormData.batteryIncluded,
          assemblyRequired: originalFormData.assemblyRequired,
          difficulty: originalFormData.difficulty,
          gameDuration: originalFormData.gameDuration,
          manufacturerCountry: originalFormData.manufacturerCountry,
        });
        setErrors({
          ...errors,
          brand: "",
          condition: "",
          warnings: "",
          category: "",
          subCategory: "",
        });
        break;

      case 4: // Tags tab
        setFormData({
          ...formData,
          tags: [...originalFormData.tags],
        });
        // Clear tag input and error
        setTagValue("");
        setTagError("");
        break;

      case 5: // Publish/Draft tab
        setFormData({
          ...formData,
          status: originalFormData.status,
        });
        break;

      default:
        break;
    }
  };

  const handleRemoveImage = imageId => {
    setImages(images.filter(img => img.id !== imageId));
  };

  const handleTabChange = (event, newValue) => {
    // If no changes, switch tab immediately
    if (!hasFormDataChanged()) {
      setActiveTab(newValue);
      return;
    }
    // If there are changes, show confirmation dialog
    setPopupConfig({
      title: `Are you sure you want to ignore the changes to ${getCurrentTabName()} tab?`,
      message: "",
      showCheckCircle: false,
      confirmText: "YES",
      cancelText: "NO",
      onConfirm: () => handleIgnoreChanges(newValue),
    });
    setShowPopup(true);
  };

  const handleIgnoreChanges = tabValue => {
    // Reset the current tab's data before switching
    resetCurrentTabData();

    // Switch to the new tab
    setActiveTab(tabValue);
  };

  const handleInputChange = field => event => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  // Handle price input with numeric validation
  const handlePriceChange = field => event => {
    const value = event.target.value;
    // Allow only numbers, decimal point, and empty string
    const numericRegex = /^[0-9]*\.?[0-9]*$/;

    if (value === "" || numericRegex.test(value)) {
      // Prevent multiple decimal points
      const decimalCount = (value.match(/\./g) || []).length;
      if (decimalCount <= 1) {
        setFormData({
          ...formData,
          [field]: value,
        });
      }
    }
  };

  const validateCurrentTab = () => {
    const newErrors = {};
    let hasErrors = false;

    switch (activeTab) {
      case 0: // Title/Desc tab
        if (!formData.title.trim()) {
          newErrors.title = "Title/Summary cannot be blank.";
          hasErrors = true;
        }
        if (!formData.description.trim()) {
          newErrors.description = "Description cannot be blank.";
          hasErrors = true;
        }
        break;

      case 1: // Image tab
        if (images.length === 0) {
          newErrors.images = "At least one image is required.";
          hasErrors = true;
        }
        break;

      case 2: // Price tab
        if (!formData.price.trim()) {
          newErrors.price = "Selling Price cannot be blank.";
          hasErrors = true;
        }
        break;

      case 3: // Attributes tab
        if (!formData.brand.trim()) {
          newErrors.brand = "Brand cannot be blank.";
          hasErrors = true;
        }
        if (!formData.condition.trim()) {
          newErrors.condition = "Condition cannot be blank.";
          hasErrors = true;
        }
        if (!formData.warnings.trim()) {
          newErrors.warnings = "Warnings cannot be blank.";
          hasErrors = true;
        }
        if (!formData.category.trim()) {
          newErrors.category = "Category cannot be blank.";
          hasErrors = true;
        }
        if (!formData.subCategory.trim()) {
          newErrors.subCategory = "Subcategory cannot be blank.";
          hasErrors = true;
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return !hasErrors;
  };

  const handleSave = () => {
    if (validateCurrentTab()) {
      // Only show save confirmation if there are changes
      if (hasFormDataChanged()) {
        // Special handling for Publish/Draft tab
        if (activeTab === 5) {
          const currentStatus = originalFormData.status;
          const newStatus = formData.status;

          if (currentStatus !== newStatus) {
            if (currentStatus === "Published" && newStatus === "Draft") {
              setPopupConfig({
                title: "Are you sure you want to unpublish this toy?",
                message: "",
                showCheckCircle: false,
                confirmText: "YES, UNPUBLISH",
                cancelText: "CANCEL",
                onConfirm: handleConfirmSave,
              });
            } else if (currentStatus === "Draft" && newStatus === "Published") {
              setPopupConfig({
                title: "Are you sure you want to publish this toy?",
                message: "",
                showCheckCircle: false,
                confirmText: "YES, PUBLISH",
                cancelText: "CANCEL",
                onConfirm: handleConfirmSave,
              });
            } else {
              setPopupConfig({
                title: `Are you sure you want to save the changes to ${getCurrentTabName()} tab?`,
                message: "",
                showCheckCircle: true,
                confirmText: "SAVE",
                cancelText: "CANCEL",
                onConfirm: handleConfirmSave,
              });
            }
          } else {
            setPopupConfig({
              title: `Are you sure you want to save the changes to ${getCurrentTabName()} tab?`,
              message: "",
              showCheckCircle: true,
              confirmText: "SAVE",
              cancelText: "CANCEL",
              onConfirm: handleConfirmSave,
            });
          }
        } else {
          setPopupConfig({
            title: `Are you sure you want to save the changes to ${getCurrentTabName()} tab?`,
            message: "",
            showCheckCircle: true,
            confirmText: "SAVE",
            cancelText: "CANCEL",
            onConfirm: handleConfirmSave,
          });
        }
        setShowPopup(true);
      } else {
        //No changes to save
        setPopupConfig({
          title: `You did not make any changes.`,
          message: "",
          showCheckCircle: false,
          confirmText: "OK",
          cancelText: null,
          onConfirm: null,
        });
        setShowPopup(true);
      }
    } else {
      // Show error popup for first empty required field on current tab
      const firstError = Object.values(errors).find(error => error);
      if (firstError) {
        setPopupConfig({
          title: `Error in ${getCurrentTabName()} tab`,
          message: firstError,
          showCheckCircle: false,
          confirmText: "CLOSE",
          cancelText: "CANCEL",
          onConfirm: null,
        });
        setShowPopup(true);
      }
    }
  };

  const handleConfirmSave = async () => {
    // ***** Save data into data base ******

    // ----- First define changed variables
    const changedData = {};
    Object.keys(formData).forEach(key => {
      // comparison for arrays specially Tags
      if (Array.isArray(formData[key])) {
        const originalArr = originalFormData[key] || [];
        const updatedArr = formData[key] || [];

        const isSame =
          originalArr.length === updatedArr.length &&
          originalArr.every((v, i) => v === updatedArr[i]);
        if (!isSame) {
          changedData[key] = updatedArr;
        }
      } else {
        // comparison for primitives and strings
        if (formData[key] !== originalFormData[key]) {
          changedData[key] = formData[key];
        }
      }
    });

    // ----- Second save data into database
    if (Object.keys(changedData).length === 0) {
      // ----- The images are changed and need to save into database
      const { data: saveToyImage, error: saveToyImageError } = await updateToyImages({
        variables: {
          toyUuid,
          imagesData: images,
        },
      });
      if (saveToyImageError) {
        console.log(saveToyImageError);
        setPopupConfig({
          title: "Error",
          message: "An error occurred.",
          confirmText: "OK",
          cancelText: "",
          onConfirm: null,
        });
        setShowPopup(true);
        return;
      } else {
        console.log(saveToyImage.updateToyImagesByUuid.success);
      }
    } else {
      const { data: saveToyData, error: saveToyDataError } = await updateToyAttrData({
        variables: {
          toyUuid,
          updateVals: changedData,
        },
      });
      if (saveToyDataError) {
        console.log(saveToyDataError);
        setPopupConfig({
          title: "Error",
          message: "An error occurred.",
          confirmText: "OK",
          cancelText: "",
          onConfirm: null,
        });
        setShowPopup(true);
        return;
      } else {
        console.log(saveToyData.updateToyByUuid.success);
      }
    }

    // Update original data to current data after successful save
    setOriginalFormData({ ...formData });
    setOriginalImages([...images]);

    setPopupConfig({
      title: "Success",
      message: "The changes has been saved.",
      confirmText: "OK",
      cancelText: "",
      onConfirm: null,
    });
    setShowPopup(true);
  };

  const handleCancel = () => {
    if (!hasFormDataChanged()) {
      console.log("Cancelled - no changes to discard");
      return;
    }

    // If there are changes, show confirmation dialog
    setPopupConfig({
      title: `Are you sure you want to ignore the changes to ${getCurrentTabName()} tab?`,
      message: "",
      showCheckCircle: false,
      confirmText: "YES",
      cancelText: "NO",
      onConfirm: handleConfirmCancel,
    });
    setShowPopup(true);
  };

  const handleConfirmCancel = () => {
    console.log("Cancelled - discarding changes for current tab");

    // Reset the current tab's data
    resetCurrentTabData();

    // Close popup
    setShowPopup(false);
  };

  const handleUploadClick = () => {
    document.getElementById("image-upload").click();
  };

  // Tag handling functions
  const cleanTag = str => str.trim().replace(/\s+/g, " ");

  const addTag = () => {
    // Normalize the input: Trim and reduce multiple spaces to one
    const trimmed = cleanTag(tagValue);

    if (!trimmed) return;

    // Validation
    if (formData.tags.some(tag => tag.toLowerCase() === trimmed.toLowerCase())) {
      setTagError("You already added this tag.");
      return;
    }
    if (formData.tags.length >= maxTags) {
      setTagError(`Maximum ${maxTags} tags allowed.`);
      return;
    }
    if (trimmed.length > maxTagLength) {
      setTagError(`Tag must be under ${maxTagLength} characters.`);
      return;
    }
    if (!validTagRegex.test(trimmed)) {
      setTagError("Only letters, numbers, dashes, underscores, and spaces are allowed.");
      return;
    }

    setFormData({
      ...formData,
      tags: [...formData.tags, trimmed],
    });
    setTagValue("");
    setTagError("");
  };

  const handleKeyDown = e => {
    if (e.key === "Enter") {
      e.preventDefault();
      addTag();
    } else if (e.key === "Backspace" && tagValue === "") {
      if (formData.tags.length > 0) {
        e.preventDefault();
        const updatedTags = [...formData.tags];
        updatedTags.pop();
        setFormData({
          ...formData,
          tags: updatedTags,
        });
        setTagError("");
      }
    }
  };

  const handleDeleteTag = tagToDelete => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToDelete),
    });
    setTagError("");
  };

  const handleTagInputChange = e => {
    setTagValue(e.target.value);
    setTagError(""); // Clear error when user starts typing
  };

  // Tab options for mobile dropdown
  const tabOptions = [
    { value: 0, label: "Title/Desc", icon: <EditOutlinedIcon /> },
    { value: 1, label: "Image", icon: <ImageOutlinedIcon /> },
    { value: 2, label: "Price", icon: <AttachMoneyOutlinedIcon /> },
    { value: 3, label: "Attributes", icon: <FormatListBulletedOutlinedIcon /> },
    { value: 4, label: "Tags", icon: <SellOutlinedIcon /> },
    { value: 5, label: "Publish/Draft", icon: <FactCheckOutlinedIcon /> },
  ];

  // Check if form data has changed
  const hasFormDataChanged = () => {
    const formChanged = JSON.stringify(formData) !== JSON.stringify(originalFormData);
    const imagesChanged = JSON.stringify(images) !== JSON.stringify(originalImages);
    return formChanged || imagesChanged;
  };

  // Handle category change and reset subcategory
  const handleCategoryChange = event => {
    const newCategory = event.target.value;
    setCurSubcategoryList(subCategoryList[newCategory]);
    setFormData({
      ...formData,
      category: newCategory,
      subCategory: "Don't Care",
    });
  };

  return (
    <>
      {showPopup === true || Object.keys(formData).length > 0 ? (
        <Box
          sx={{
            backgroundColor: "#f5f5f5",
            minHeight: "100vh",
            px: "100px",
            "@media (max-width: 1360px)": {
              px: "16px",
            },
            py: 3,
          }}
        >
          <Helmet>
            <title>R3PLAY - Edit Toy</title>
            <meta
              name="description"
              content="Update your toy’s information, images and price on R3PLAY."
            />
            <meta property="keywords" content="Edit toy, update toy, toy management" />
          </Helmet>

          {formData && Object.keys(formData).length > 0 && (
            <div>
              {/* Tab Box - Desktop */}
              {!isMobile && (
                <Paper
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "12px",
                    height: "68px",
                    p: "16px",
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <StyledTabs
                    value={activeTab}
                    onChange={handleTabChange}
                    centered
                    sx={{ width: "100%" }}
                  >
                    <StyledTab icon={<EditOutlinedIcon />} label="Title/Desc" />
                    <StyledTab icon={<ImageOutlinedIcon />} label="Image" />
                    <StyledTab icon={<AttachMoneyOutlinedIcon />} label="Price" />
                    <StyledTab
                      icon={<FormatListBulletedOutlinedIcon />}
                      label="Attributes"
                    />
                    <StyledTab icon={<SellOutlinedIcon />} label="Tags" />
                    <StyledTab icon={<FactCheckOutlinedIcon />} label="Publish/Draft" />
                  </StyledTabs>
                </Paper>
              )}

              {/* Tab Dropdown - Mobile */}
              {isMobile && (
                <Paper
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "12px",
                    p: "16px",
                    mb: 2,
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <FormControl sx={{ minWidth: 200 }}>
                    <Select
                      value={activeTab}
                      onChange={e => handleTabChange(e, e.target.value)}
                      displayEmpty
                      sx={{
                        height: "40px",
                        "& .MuiSelect-select": {
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        },
                        "&:hover": {
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(0, 0, 0, 0.23)",
                          },
                        },
                        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                          {
                            borderColor: "rgba(0, 0, 0, 0.23)",
                          },
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                          {
                            borderColor: "rgba(0, 0, 0, 0.23)",
                            borderWidth: "1px",
                          },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(0, 0, 0, 0.23)",
                          borderWidth: "1px",
                        },
                      }}
                    >
                      {tabOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {option.icon}
                            {option.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              )}

              {/* Content Space */}

              <TabPanel value={activeTab} index={0}>
                <Paper sx={{ backgroundColor: "white", borderRadius: "12px", p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <EditOutlinedIcon sx={{ color: "#EF5658", mr: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      Desc/Title{" "}
                      <span style={{ fontSize: "14px", color: "#666" }}>
                        (Must fill before posting)
                      </span>
                    </Typography>
                  </Box>

                  <TextField
                    fullWidth
                    label="Title/Summary"
                    placeholder="Enter Title/Summary"
                    variant="outlined"
                    required
                    value={formData.title}
                    onChange={handleInputChange("title")}
                    error={!!errors.title}
                    helperText={errors.title}
                    sx={{
                      mb: 3,
                      "& .MuiInputBase-root": { height: "56px" },
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Description"
                    placeholder="Enter Description"
                    variant="outlined"
                    required
                    multiline
                    minRows={4}
                    maxRows={10}
                    value={formData.description}
                    onChange={handleInputChange("description")}
                    error={!!errors.description}
                    helperText={errors.description}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 2,
                      mt: 3,
                    }}
                  >
                    <Button
                      onClick={handleSave}
                      variant="contained"
                      sx={{
                        backgroundColor: "#EF5658",
                        color: "white",
                        borderRadius: "4px",
                        py: 1.5,
                        height: "36px",
                        fontSize: "14px",
                        fontWeight: "medium",
                        textTransform: "uppercase",
                        minWidth: "140px",
                        "&:hover": {
                          backgroundColor: "#D33338",
                        },
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      onClick={handleCancel}
                      variant="outlined"
                      sx={{
                        borderColor: "#666",
                        color: "#666",
                        borderRadius: "4px",
                        px: 3,
                        py: 1.5,
                        fontSize: "14px",
                        fontWeight: "medium",
                        height: "36px",
                        textTransform: "uppercase",
                        maxWidth: "140px",
                        "&:hover": {
                          borderColor: "#666",
                          backgroundColor: "rgba(0,0,0,0.04)",
                        },
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Paper>
              </TabPanel>

              <TabPanel value={activeTab} index={1}>
                <Paper
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "12px",
                    p: 3,
                    "@media (max-width: 856px)": {
                      paddingLeft: "14px",
                      paddingRight: "14px",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <ImageOutlinedIcon sx={{ color: "#EF5658", mr: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      Image
                    </Typography>
                  </Box>

                  {/* File Upload Area */}
                  <Box
                    {...getRootProps()}
                    sx={{
                      border: "2px dashed #ddd",
                      borderRadius: "12px",
                      p: 2,
                      textAlign: "center",
                      mb: 3,
                      backgroundColor: "white",
                      cursor: "pointer",
                      height: "228px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      bgcolor: isDragActive ? "grey.100" : "transparent",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      {...getInputProps()}
                      type="file"
                      style={{ display: "none" }}
                      accept="image/*"
                      onChange={selectImageFunc}
                    />

                    <label>
                      <FileUploadOutlinedIcon
                        sx={{
                          fontSize: 56,
                          color: "#999",
                          mb: 2,
                        }}
                      />
                      {isDragActive ? (
                        <Typography
                          sx={{
                            fontWeight: 600,
                            color: "#E53935",
                            mb: 1,
                            fontSize: "20px",
                          }}
                        >
                          Drop photos here...
                        </Typography>
                      ) : (
                        <Typography
                          sx={{
                            fontWeight: 600,
                            color: "#333",
                            mb: 1,
                            fontSize: "20px",
                          }}
                        >
                          Drop photos here or click to browse
                        </Typography>
                      )}
                      <Box
                        sx={{
                          color: "#666",
                          mb: 1,
                          fontSize: "16px",
                        }}
                      >
                        Support for JPG, PNG, WEBP formats
                      </Box>
                      <Box
                        sx={{
                          color: "#666",
                          fontSize: "14px",
                        }}
                      >
                        Max 5MB per image, Up to 6 images
                      </Box>
                    </label>
                  </Box>
                  {/* Image Display */}
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
                    {images.map(image => (
                      <Box
                        key={image.id}
                        sx={{
                          position: "relative",
                          width: "174.67px",
                          "@media (max-width: 856px)": {
                            width: "156px",
                          },
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveImage(image.id)}
                          sx={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            "@media (max-width: 856px)": {
                              top: 6,
                              right: 6,
                            },
                            backgroundColor: "white",
                            boxShadow: 1,
                            zIndex: 1,
                            width: "24px",
                            height: "24px",
                            "&:hover": {
                              backgroundColor: "#f5f5f5",
                            },
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                        <Box
                          component="img"
                          src={image.src}
                          alt={image.name}
                          sx={{
                            width: "174.67px",
                            height: "174.67px",
                            objectFit: "cover",
                            borderRadius: "4px",
                            border: "1px solid #ddd",
                            "@media (max-width: 856px)": {
                              width: "156px",
                              height: "156px",
                            },
                          }}
                        />
                        <Box sx={{ textAlign: "center", mt: 2 }}>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: "#666",
                            }}
                          >
                            File: {image.name}
                          </Typography>
                          {/* <Typography
                        sx={{
                          mt: 1,
                          color: "#999",
                          fontSize: "12px",
                        }}
                      >
                        Size:{" "}
                        {image.size < 1024 * 1024
                          ? `${Math.round(image.size / 1024)} KB`
                          : `${(image.size / (1024 * 1024)).toFixed(2)} MB`}
                      </Typography> */}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 2,
                      mt: 3,
                    }}
                  >
                    <Button
                      onClick={handleSave}
                      variant="contained"
                      sx={{
                        backgroundColor: "#EF5658",
                        color: "white",
                        borderRadius: "4px",
                        py: 1.5,
                        height: "36px",
                        fontSize: "14px",
                        fontWeight: "medium",
                        textTransform: "uppercase",
                        minWidth: "140px",
                        "&:hover": {
                          backgroundColor: "#D33338",
                        },
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      onClick={handleCancel}
                      variant="outlined"
                      sx={{
                        borderColor: "#666",
                        color: "#666",
                        borderRadius: "4px",
                        px: 3,
                        py: 1.5,
                        fontSize: "14px",
                        fontWeight: "medium",
                        height: "36px",
                        textTransform: "uppercase",
                        maxWidth: "140px",
                        "&:hover": {
                          borderColor: "#666",
                          backgroundColor: "rgba(0,0,0,0.04)",
                        },
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Paper>
              </TabPanel>

              <TabPanel value={activeTab} index={2}>
                <Paper sx={{ backgroundColor: "white", borderRadius: "12px", p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <PaidOutlinedIcon sx={{ color: "#EF5658", mr: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      Price{" "}
                      <span style={{ fontSize: "14px", color: "#666" }}>
                        (Must fill before posting)
                      </span>
                    </Typography>
                  </Box>

                  <TextField
                    fullWidth
                    label="Selling Price ($)"
                    placeholder="Enter Selling Price"
                    variant="outlined"
                    required
                    value={formData.price}
                    onChange={handlePriceChange("price")}
                    error={!!errors.price}
                    helperText={errors.price}
                    sx={{
                      mb: 3,
                      "& .MuiInputBase-root": { height: "56px" },
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Original Price ($)"
                    placeholder="Enter Original Price"
                    variant="outlined"
                    value={formData.originalPrice}
                    onChange={handlePriceChange("originalPrice")}
                    sx={{
                      "& .MuiInputBase-root": { height: "56px" },
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 2,
                      mt: 3,
                    }}
                  >
                    <Button
                      onClick={handleSave}
                      variant="contained"
                      sx={{
                        backgroundColor: "#EF5658",
                        color: "white",
                        borderRadius: "4px",
                        py: 1.5,
                        height: "36px",
                        fontSize: "14px",
                        fontWeight: "medium",
                        textTransform: "uppercase",
                        minWidth: "140px",
                        "&:hover": {
                          backgroundColor: "#D33338",
                        },
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      onClick={handleCancel}
                      variant="outlined"
                      sx={{
                        borderColor: "#666",
                        color: "#666",
                        borderRadius: "4px",
                        px: 3,
                        py: 1.5,
                        fontSize: "14px",
                        fontWeight: "medium",
                        height: "36px",
                        textTransform: "uppercase",
                        maxWidth: "140px",
                        "&:hover": {
                          borderColor: "#666",
                          backgroundColor: "rgba(0,0,0,0.04)",
                        },
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Paper>
              </TabPanel>

              <TabPanel value={activeTab} index={3}>
                {/* Required Information Card */}
                <Paper
                  sx={{ backgroundColor: "white", borderRadius: "12px", p: 3, mb: 2 }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <ListAltOutlinedIcon sx={{ color: "#EF5658", mr: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      Required Information{" "}
                      <span style={{ fontSize: "14px", color: "#666" }}>
                        (Must fill before posting)
                      </span>
                    </Typography>
                  </Box>
                  {/* Category and Subcategory Row */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      mb: 3,
                      "@media (max-width: 856px)": {
                        flexDirection: "column",
                        gap: 3,
                      },
                    }}
                  >
                    {/* Category Dropdown */}
                    <StyledFormControl sx={{ flex: 1 }}>
                      <InputLabel id="category-select-label" required>
                        Category
                      </InputLabel>
                      <Select
                        labelId="category-select-label"
                        id="category-select"
                        value={formData.category}
                        label="Category"
                        required
                        onChange={handleCategoryChange}
                        error={!!errors.category}
                      >
                        <MenuItem value="" disabled>
                          Select Category
                        </MenuItem>
                        {categoryList.map(option => (
                          <MenuItem value={option}>{option}</MenuItem>
                        ))}
                      </Select>
                      {errors.category && (
                        <FormHelperText error>{errors.category}</FormHelperText>
                      )}
                    </StyledFormControl>

                    {/* Subcategory Dropdown */}
                    <StyledFormControl sx={{ flex: 2 }}>
                      <InputLabel id="subcategory-select-label" required>
                        Subcategory
                      </InputLabel>
                      <Select
                        labelId="subcategory-select-label"
                        id="subcategory-select"
                        value={formData.subCategory}
                        label="Subcategory"
                        required
                        disabled={!formData.category}
                        onChange={handleInputChange("subCategory")}
                        error={!!errors.subCategory}
                      >
                        <MenuItem value="" disabled>
                          Select Subcategory
                        </MenuItem>
                        {curSubcategoryList.map(option => (
                          <MenuItem value={option}>{option}</MenuItem>
                        ))}
                      </Select>
                      {errors.subCategory && (
                        <FormHelperText error>{errors.subCategory}</FormHelperText>
                      )}
                    </StyledFormControl>
                  </Box>

                  {/* Brand Dropdown */}
                  <StyledFormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel id="brand-select-label" required>
                      Brand
                    </InputLabel>
                    <Select
                      labelId="brand-select-label"
                      id="brand-select"
                      value={formData.brand}
                      label="Brand"
                      required
                      onChange={handleInputChange("brand")}
                      error={!!errors.brand}
                    >
                      <MenuItem value="" disabled>
                        Select Brand
                      </MenuItem>
                      {brandList.map(option => (
                        <MenuItem value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                    {errors.brand && (
                      <FormHelperText error>{errors.brand}</FormHelperText>
                    )}
                  </StyledFormControl>

                  {/* Condition Dropdown */}
                  <StyledFormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel id="condition-select-label" required>
                      Condition
                    </InputLabel>
                    <Select
                      labelId="condition-select-label"
                      id="condition-select"
                      value={formData.condition}
                      label="Condition"
                      required
                      onChange={handleInputChange("condition")}
                      error={!!errors.condition}
                    >
                      <MenuItem value="" disabled>
                        Select Condition
                      </MenuItem>
                      {conditionList.map(option => (
                        <MenuItem value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                    {errors.condition && (
                      <FormHelperText error>{errors.condition}</FormHelperText>
                    )}
                  </StyledFormControl>

                  {/* Warnings Dropdown */}
                  <StyledFormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel id="warnings-select-label" required>
                      Warnings
                    </InputLabel>
                    <Select
                      labelId="warnings-select-label"
                      id="warnings-select"
                      value={formData.warnings}
                      label="Warnings"
                      required
                      onChange={handleInputChange("warnings")}
                      error={!!errors.warnings}
                    >
                      <MenuItem value="" disabled>
                        Select Warnings
                      </MenuItem>
                      {warningList.map(option => (
                        <MenuItem value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                    {errors.warnings && (
                      <FormHelperText error>{errors.warnings}</FormHelperText>
                    )}
                  </StyledFormControl>
                </Paper>

                {/* Optional Information Card  */}
                <Paper
                  sx={{ backgroundColor: "white", borderRadius: "12px", p: 3, mb: 2 }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <ListAltOutlinedIcon sx={{ color: "#EF5658", mr: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      Optional Information
                    </Typography>
                  </Box>

                  {/* Age Group Dropdown */}
                  <StyledOptionalFormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel id="age-group-select-label">Age Group (years)</InputLabel>
                    <Select
                      labelId="age-group-select-label"
                      id="age-group-select"
                      value={formData.ageGroup}
                      label="Age Group (years)"
                      onChange={handleInputChange("ageGroup")}
                    >
                      <MenuItem value="" disabled>
                        Select Age Group
                      </MenuItem>
                      {ageGroupList.map(option => (
                        <MenuItem value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                  </StyledOptionalFormControl>

                  {/* Material Dropdown */}
                  <StyledOptionalFormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel id="material-select-label">Material</InputLabel>
                    <Select
                      labelId="material-select-label"
                      id="material-select"
                      value={formData.material}
                      label="Material"
                      onChange={handleInputChange("material")}
                    >
                      <MenuItem value="" disabled>
                        Select Material
                      </MenuItem>
                      {materialList.map(option => (
                        <MenuItem value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                  </StyledOptionalFormControl>

                  {/* Dimensions Text Field */}
                  <TextField
                    fullWidth
                    label="Dimension LxWxH (inch)"
                    placeholder="Enter Dimensions"
                    variant="outlined"
                    value={formData.dimension}
                    onChange={handleInputChange("dimension")}
                    sx={{
                      mb: 3,
                      "& .MuiInputBase-root": { height: "56px" },
                    }}
                  />

                  {/* Weight Text Field */}
                  <TextField
                    fullWidth
                    label="Weight (lbs)"
                    placeholder="Enter Weight"
                    variant="outlined"
                    value={formData.weight}
                    onChange={handleInputChange("weight")}
                    sx={{
                      mb: 3,
                      "& .MuiInputBase-root": { height: "56px" },
                    }}
                  />

                  {/* Color Dropdown */}
                  <StyledOptionalFormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel id="color-select-label">Color</InputLabel>
                    <Select
                      labelId="color-select-label"
                      id="color-select"
                      value={formData.color}
                      label="Color"
                      onChange={handleInputChange("color")}
                    >
                      <MenuItem value="" disabled>
                        Select Color
                      </MenuItem>
                      {colorList.map(option => (
                        <MenuItem value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                  </StyledOptionalFormControl>

                  {/* Sub-Color Dropdown */}
                  <StyledOptionalFormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel id="sub-color-select-label">Sub-Color</InputLabel>
                    <Select
                      labelId="sub-color-select-label"
                      id="sub-color-select"
                      value={formData.subColor}
                      label="Sub-Color"
                      onChange={handleInputChange("subColor")}
                    >
                      <MenuItem value="" disabled>
                        Select Color
                      </MenuItem>
                      {subColorList.map(option => (
                        <MenuItem value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                  </StyledOptionalFormControl>

                  {/* No of Players Text Field */}
                  <TextField
                    fullWidth
                    label="No of Players"
                    placeholder="Enter No of Players"
                    variant="outlined"
                    value={formData.noOfPlayers}
                    onChange={handleInputChange("noOfPlayers")}
                    sx={{
                      mb: 1,
                      "& .MuiInputBase-root": { height: "56px" },
                    }}
                  />
                  {/* Switch Controls Row */}
                  <Box
                    sx={{
                      pt: 2,
                      display: "flex",
                      justifyContent: "space-between",
                      width: "800px",
                      mb: 3,
                      "@media (max-width: 856px)": {
                        flexDirection: "column",
                        gap: 2,
                      },
                    }}
                  >
                    {/* Battery Required Switch */}
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, fontSize: "16px" }}>
                        Battery Required
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body1">No</Typography>
                        <Switch
                          checked={formData.batteryRequired}
                          onChange={e =>
                            setFormData({
                              ...formData,
                              batteryRequired: e.target.checked,
                            })
                          }
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "#EF5658",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              backgroundColor: "#EF5658",
                            },
                          }}
                        />
                        <Typography variant="body1">Yes</Typography>
                      </Box>
                    </Box>

                    {/* Battery Included Switch */}
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, fontSize: "16px" }}>
                        Battery Included
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body1">No</Typography>
                        <Switch
                          checked={formData.batteryIncluded}
                          onChange={e =>
                            setFormData({
                              ...formData,
                              batteryIncluded: e.target.checked,
                            })
                          }
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "#EF5658",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              backgroundColor: "#EF5658",
                            },
                          }}
                        />
                        <Typography variant="body1">Yes</Typography>
                      </Box>
                    </Box>

                    {/* Assembly Required Switch */}
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, fontSize: "16px" }}>
                        Assembly Required
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body1">No</Typography>
                        <Switch
                          checked={formData.assemblyRequired}
                          onChange={e =>
                            setFormData({
                              ...formData,
                              assemblyRequired: e.target.checked,
                            })
                          }
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "#EF5658",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              backgroundColor: "#EF5658",
                            },
                          }}
                        />
                        <Typography variant="body1">Yes</Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Level of Difficulty Dropdown */}
                  <StyledOptionalFormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel id="difficulty-select-label">
                      Level of Difficulty
                    </InputLabel>
                    <Select
                      labelId="difficulty-select-label"
                      id="difficulty-select"
                      value={formData.difficulty}
                      label="Level of Difficulty"
                      onChange={handleInputChange("difficulty")}
                    >
                      <MenuItem value="" disabled>
                        Select Difficulty
                      </MenuItem>
                      {difficultyList.map(option => (
                        <MenuItem value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                  </StyledOptionalFormControl>

                  {/* Game Duration Text Field */}
                  <TextField
                    fullWidth
                    label="Game Duration (Min.)"
                    placeholder="Enter Game Duration"
                    variant="outlined"
                    value={formData.gameDuration}
                    onChange={handleInputChange("gameDuration")}
                    sx={{
                      mb: 3,
                      "& .MuiInputBase-root": { height: "56px" },
                    }}
                  />

                  {/* Manufacturer Country Dropdown */}
                  <StyledOptionalFormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel id="manufacturer-country-select-label">
                      Manufacturer Country
                    </InputLabel>
                    <Select
                      labelId="manufacturer-country-select-label"
                      id="manufacturer-country-select"
                      value={formData.manufacturerCountry}
                      label="Manufacturer Country"
                      onChange={handleInputChange("manufacturerCountry")}
                    >
                      <MenuItem value="" disabled>
                        Select Country
                      </MenuItem>
                      {countryList.map(option => (
                        <MenuItem value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                  </StyledOptionalFormControl>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 2,
                      mt: 3,
                    }}
                  >
                    <Button
                      onClick={handleSave}
                      variant="contained"
                      sx={{
                        backgroundColor: "#EF5658",
                        color: "white",
                        borderRadius: "4px",
                        py: 1.5,
                        height: "36px",
                        fontSize: "14px",
                        fontWeight: "medium",
                        textTransform: "uppercase",
                        minWidth: "140px",
                        "&:hover": {
                          backgroundColor: "#D33338",
                        },
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      onClick={handleCancel}
                      variant="outlined"
                      sx={{
                        borderColor: "#666",
                        color: "#666",
                        borderRadius: "4px",
                        px: 3,
                        py: 1.5,
                        fontSize: "14px",
                        fontWeight: "medium",
                        height: "36px",
                        textTransform: "uppercase",
                        maxWidth: "140px",
                        "&:hover": {
                          borderColor: "#666",
                          backgroundColor: "rgba(0,0,0,0.04)",
                        },
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Paper>
              </TabPanel>

              <TabPanel value={activeTab} index={4}>
                <Paper sx={{ backgroundColor: "white", borderRadius: "12px", p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <SellOutlinedIcon sx={{ color: "#EF5658", mr: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      Tags
                    </Typography>
                  </Box>

                  {/* Tags Input Section */}
                  <Box sx={{ mb: 2 }}>
                    {/* Tags Display and Input */}
                    <Box
                      sx={{
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        p: 1,
                        minHeight: "56px",
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 1,
                        backgroundColor: "white",
                      }}
                    >
                      {/* Display existing tags */}
                      {formData.tags.map((tag, index) => (
                        <Chip
                          key={index}
                          label={tag}
                          onDelete={() => handleDeleteTag(tag)}
                          sx={{
                            backgroundColor: "rgba(0, 0, 0, 0.08)",
                            color: "rgba(0, 0, 0, 0.87)",
                            height: "28px",
                            fontSize: "14px",
                            "& .MuiChip-deleteIcon": {
                              color: "rgba(0, 0, 0, 0.30)",
                              fontSize: "20px",
                              "&:hover": {
                                color: "rgba(0, 0, 0, 0.50)",
                              },
                            },
                          }}
                        />
                      ))}

                      {/* Input for new tags */}
                      <InputBase
                        value={tagValue}
                        onChange={handleTagInputChange}
                        onKeyDown={handleKeyDown}
                        placeholder={
                          formData.tags.length === 0
                            ? isMobile
                              ? "Add tags"
                              : "Add tags and press Enter"
                            : ""
                        }
                        sx={{
                          flex: 1,
                          minWidth: "120px",
                          fontSize: "14px",
                          color: "#333",
                          "& input": {
                            padding: "8px 4px",
                          },
                        }}
                      />

                      {/* Add Tag Icon - Mobile/Tablet Only - Show when typing */}
                      {isMobile && tagValue.trim() && (
                        <IconButton
                          onClick={addTag}
                          size="small"
                          sx={{ ml: 0.5 }}
                          aria-label="Add tag"
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>

                    {/* Tag Error Message */}
                    {tagError && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "red",
                          fontSize: "12px",
                          mt: 1,
                          ml: 1,
                        }}
                      >
                        {tagError}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 2,
                      mt: 3,
                    }}
                  >
                    <Button
                      onClick={handleSave}
                      variant="contained"
                      sx={{
                        backgroundColor: "#EF5658",
                        color: "white",
                        borderRadius: "4px",
                        py: 1.5,
                        height: "36px",
                        fontSize: "14px",
                        fontWeight: "medium",
                        textTransform: "uppercase",
                        minWidth: "140px",
                        "&:hover": {
                          backgroundColor: "#D33338",
                        },
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      onClick={handleCancel}
                      variant="outlined"
                      sx={{
                        borderColor: "#666",
                        color: "#666",
                        borderRadius: "4px",
                        px: 3,
                        py: 1.5,
                        fontSize: "14px",
                        fontWeight: "medium",
                        height: "36px",
                        textTransform: "uppercase",
                        maxWidth: "140px",
                        "&:hover": {
                          borderColor: "#666",
                          backgroundColor: "rgba(0,0,0,0.04)",
                        },
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Paper>
              </TabPanel>

              <TabPanel value={activeTab} index={5}>
                <Paper sx={{ backgroundColor: "white", borderRadius: "12px", p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <FactCheckOutlinedIcon sx={{ color: "#EF5658", mr: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      Publish/Draft
                    </Typography>
                  </Box>

                  {/* Status Radio Group */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                      Status
                    </Typography>
                    <RadioGroup
                      value={formData.status}
                      onChange={handleInputChange("status")}
                      sx={{ gap: 1 }}
                    >
                      <FormControlLabel
                        value="Published"
                        control={
                          <Radio
                            sx={{
                              color: "#EF5658",
                              "&.Mui-checked": { color: "#EF5658" },
                            }}
                          />
                        }
                        label="Published"
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontSize: "16px",
                            fontWeight: 400,
                          },
                        }}
                      />
                      <FormControlLabel
                        value="Draft"
                        control={
                          <Radio
                            sx={{
                              color: "#EF5658",
                              "&.Mui-checked": { color: "#EF5658" },
                            }}
                          />
                        }
                        label="Draft"
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontSize: "16px",
                            fontWeight: 400,
                          },
                        }}
                      />
                    </RadioGroup>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 2,
                      mt: 3,
                    }}
                  >
                    <Button
                      onClick={handleSave}
                      variant="contained"
                      sx={{
                        backgroundColor: "#EF5658",
                        color: "white",
                        borderRadius: "4px",
                        py: 1.5,
                        height: "36px",
                        fontSize: "14px",
                        fontWeight: "medium",
                        textTransform: "uppercase",
                        minWidth: "140px",
                        "&:hover": {
                          backgroundColor: "#D33338",
                        },
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      onClick={handleCancel}
                      variant="outlined"
                      sx={{
                        borderColor: "#666",
                        color: "#666",
                        borderRadius: "4px",
                        px: 3,
                        py: 1.5,
                        fontSize: "14px",
                        fontWeight: "medium",
                        height: "36px",
                        textTransform: "uppercase",
                        maxWidth: "140px",
                        "&:hover": {
                          borderColor: "#666",
                          backgroundColor: "rgba(0,0,0,0.04)",
                        },
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Paper>
              </TabPanel>
            </div>
          )}

          <AlertPopup
            open={showPopup}
            onClose={() => setShowPopup(false)}
            title={popupConfig.title}
            message={popupConfig.message}
            showCheckCircle={popupConfig.showCheckCircle}
            confirmText={popupConfig.confirmText}
            cancelText={popupConfig.cancelText}
            onConfirm={popupConfig.onConfirm}
          />

          <AlertPopup
            open={alertPopup.open}
            title={alertPopup.title}
            message=""
            confirmText="OK"
            cancelText=""
            onConfirm={() => setAlertPopup(false)}
            onClose={() => setAlertPopup(false)}
          />

          <FloatingMenu left={floatMenuLeftMargin} />
        </Box>
      ) : (
        isInvalidURL && (
          <Box sx={{ textAlign: "center", mb: 4, mt: 10 }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: "30px",
                "@media (max-width: 856px)": {
                  fontSize: "24px",
                },
                fontWeight: "700",
                color: "#333",
                mb: 1,
              }}
            >
              This URL is not valid.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate("/dashboard")}
              sx={{
                height: "42px",
                px: 3,
                mt: 5,
                fontWeight: 500,
                backgroundColor: "#EF5658",
                "@media (max-width: 856px)": {
                  fontSize: "13px",
                  height: "30px",
                  px: 1,
                },
              }}
            >
              OK
            </Button>
          </Box>
        )
      )}
    </>
  );
};

export default ToyEdit;
